import {
  createBaseThemeOptions,
  createUnifiedTheme,
  PageTheme,
  pageTheme as defaultPageThemes,
  palettes,
} from '@backstage/theme';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const pageThemesFontColorOverride: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).map(key => {
  pageThemesFontColorOverride[key] = {
    ...defaultPageThemes[key],
    fontColor: '#172B4D',
  };
});

const eonColorPallette = {
  eonColors: {
    white: '#FFFFFF',
    red: '#EA1B0A',
  },
  basicColors: {
    white: '#FFFFFF',
  },
  primary: {
    main: '#EA1B0A',
    light: '#EA1B0A',
    dark: '#EA1B0A',
  },
  secondary: {
    main: '#961482',
    light: '#961482',
    dark: '#961482',
  },
  grey: {
    100: '#F8F5F2',
    200: '#F0EBE5',
    300: '#D7D0CC',
    400: '#B3ACA9',
    500: '#958D8B',
    600: '#767676',
    700: '#5C5C5C',
    800: '#404040',
    900: '#262626',
  },
  error: {
    main: '#FF5630',
    light: '#FF8F73',
    dark: '#DE350B',
  },
  warning: {
    main: '#FFAB00',
    light: '#FFE380',
    dark: '#FF8B00',
  },
  success: {
    main: '#36B37E',
    light: '#79F2C0',
    dark: '#006644',
  },
  info: {
    main: '#0065FF',
    light: '#4C9AFF',
    dark: '#0747A6',
  },
};

export const eonTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    fontFamily: 'EONBrixSans',
    palette: {
      ...palettes.light,
      primary: eonColorPallette.primary,
      secondary: eonColorPallette.secondary,
      grey: eonColorPallette.grey,
      error: eonColorPallette.error,
      warning: eonColorPallette.warning,
      success: eonColorPallette.success,
      info: eonColorPallette.info,
      navigation: {
        ...palettes.light.navigation,
        background: '#404040',
        color: eonColorPallette.eonColors.white,
        indicator: eonColorPallette.eonColors.white,
        navItem: {
          hoverBackground: 'rgba(240, 91, 72,0.6)',
        },
      },
      text: {
        primary: '#000000',
      },
      background: {
        default: eonColorPallette.eonColors.white,
      },
    },
  }),
  typography: {
    htmlFontSize: 16,
    fontFamily: 'EONBrixSans',
    h1: {
      fontSize: 54,
      fontWeight: 700,
      marginBottom: 10,
    },
    h2: {
      fontSize: 40,
      fontWeight: 700,
      marginBottom: 8,
    },
    h3: {
      fontSize: 32,
      fontWeight: 700,
      marginBottom: 6,
    },
    h4: {
      fontWeight: 700,
      fontSize: 28,
      marginBottom: 6,
    },
    h5: {
      fontWeight: 700,
      fontSize: 22,
      marginBottom: 4,
    },
    h6: {
      fontWeight: 700,
      fontSize: 20,
      marginBottom: 2,
    },
  },
  pageTheme: pageThemesFontColorOverride,
  defaultPageTheme: 'home',
  components: {
    CatalogReactEntitySearchBar: {
      styleOverrides: {
        input: {
          backgroundColor: eonColorPallette.basicColors.white,
          borderRadius: 8,
          padding: '8px 12px',
          width: 380,
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundImage: 'unset',
          boxShadow: 'unset',
          display: 'flex',
          background:
            'linear-gradient(135deg, rgb(234,27,10) 0%, rgb(150,20,130) 100%) !important',
        },
        title: {
          color: eonColorPallette.eonColors.white,
          fontWeight: 900,
        },
        subtitle: {
          color: eonColorPallette.eonColors.white,
        },
        type: {
          color: eonColorPallette.eonColors.white,
          fontWeight: '800',
        },
        breadcrumb: {
          color: eonColorPallette.eonColors.white,
        },
      },
    },
    BackstageHeaderLabel: {
      styleOverrides: {
        root: {
          display: 'none',
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        defaultTab: {
          fontSize: 'inherit',
          textTransform: 'none',
        },
        tabsWrapper: {
          paddingLeft: '0',
          borderBottomWidth: 2,
          borderBottomStyle: 'solid',
          borderBottomColor: eonColorPallette.grey['600'],
          height: 64,
          marginBottom: 0,
          marginLeft: 'auto',
          marginRight: 'auto',
          maxWidth: 1440,
        },
        tabRoot: {
          fontSize: 18,
          fontWeight: 100,
          textTransform: 'none',
          color: eonColorPallette.grey[900],
          '&:hover': {
            backgroundColor: eonColorPallette.grey[100],
          },
          '&.Mui-selected': {
            backgroundColor: eonColorPallette.basicColors.white,
            fontWeight: 800,
          },
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          height: '100%',
        },
      },
    },
    BackstageContent: {
      styleOverrides: {
        root: {
          backgroundColor: eonColorPallette.grey[100], // Background color for the entire width
          padding: 0,
          margin: 0,
          overflow: 'hidden', // Prevents horizontal scrollbar
          '& > *': {
            maxWidth: 1440,
            marginLeft: 'auto',
            marginRight: 'auto',
            paddingLeft: 0,
            paddingRight: 0,
          },
          '@media (min-width: 600px)': {
            '& > *': {
              paddingLeft: 0,
              paddingRight: 0,
            },
          },
        },
      },
    },
    BackstageOpenedDropdown: {
      styleOverrides: {
        icon: {
          '& path': {
            fill: eonColorPallette.basicColors.white,
          },
        },
      },
    },
    BackstageTable: {
      styleOverrides: {
        root: {
          backgroundColor: eonColorPallette.eonColors.red,
          height: '100%',
          '&> :first-child': {
            borderBottom: '1px solid #D5D5D5',
            boxShadow: 'none',
            height: '100%',
            backgroundColor: eonColorPallette.basicColors.white,
          },
          '& th': {
            borderTop: 'none',
            textTransform: 'none !important',
            backgroundColor: eonColorPallette.eonColors.red,
            color: '#F8F5F2 !important',
          },
        },
      },
    },
    BackstageTableToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: eonColorPallette.eonColors.white,
        },
      },
    },
    BackstageSelect: {
      styleOverrides: {
        formControl: {
          maxWidth: '100%',
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        selected: {
          color: '#f8f5f2 !important',
        },
      },
    },
    CatalogReactUserListPicker: {
      styleOverrides: {
        title: {
          textTransform: 'none',
        },
        root: {
          backgroundColor: eonColorPallette.basicColors.white,
        },
      },
    },
    BackstageHorizontalScrollGrid: {
      styleOverrides: {
        container: {
          height: '100%',
          backgroundColor: eonColorPallette.basicColors.white,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
        standardError: ({ theme }) => ({
          color: eonColorPallette.basicColors.white,
          backgroundColor: theme.palette.error.dark,
          '& $icon': {
            color: eonColorPallette.basicColors.white,
          },
        }),
        standardInfo: ({ theme }) => ({
          color: eonColorPallette.basicColors.white,
          backgroundColor: theme.palette.primary.dark,
          '& $icon': {
            color: eonColorPallette.basicColors.white,
          },
        }),
        standardSuccess: ({ theme }) => ({
          color: eonColorPallette.basicColors.white,
          backgroundColor: theme.palette.success.dark,
          '& $icon': {
            color: eonColorPallette.basicColors.white,
          },
        }),
        standardWarning: ({ theme }) => ({
          color: eonColorPallette.eonColors.white,
          backgroundColor: theme.palette.primary.light,
          '& $icon': {
            color: eonColorPallette.eonColors.white,
          },
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '&[aria-expanded=true]': {
            backgroundColor: 'unset',
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(9,30,69,0.54)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
        },
        outlined: {
          borderWidth: 2,
          borderColor: eonColorPallette.grey['900'],
        },

        contained: {
          boxShadow: 'none',
          backgroundColor: eonColorPallette.basicColors.white,
          color: '#000000',
          fontSize: '1.5rem',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: eonColorPallette.grey[900],
          color: eonColorPallette.eonColors.white,
          fontSize: 14,
          fontFamily: 'inherit',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          borderWidth: 2,
          borderColor: eonColorPallette.grey['900'],
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderRadius: 8,
          borderWidth: 2,
          borderColor: eonColorPallette.grey['900'],
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          borderWidth: 2,
          borderColor: eonColorPallette.grey['900'],
        },
        select: {
          '&[aria-expanded]': {
            backgroundColor: eonColorPallette.eonColors.red,
            color: eonColorPallette.basicColors.white,
          },
        },
      },
      defaultProps: {
        IconComponent: ExpandMoreIcon,
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 10,
        },
        switchBase: {
          padding: 12,
          color: 'black',
        },
        thumb: {
          backgroundColor: eonColorPallette.basicColors.white,
          height: 14,
          width: 14,
        },
        track: {
          borderRadius: 9,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          transition: 'none',
          backgroundColor: eonColorPallette.grey[900],
        },
        root: {
          background: eonColorPallette.basicColors.white,
          height: 62,
          marginBottom: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: 18,
          fontWeight: 100,
          textTransform: 'none',
          height: 62,
          '&:hover': {
            backgroundColor: eonColorPallette.grey[100],
            color: eonColorPallette.grey[900],
          },
          '&.Mui-selected': {
            backgroundColor: eonColorPallette.grey[200],
            color: eonColorPallette.grey[900],
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        button: {
          textTransform: 'none',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: eonColorPallette.eonColors.red,
          color: eonColorPallette.basicColors.white,
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: eonColorPallette.eonColors.white,
          '&:hover': {
            color: '#F8F5F2 !important',
          },
        },
        active: {
          color: '#F8F5F2 !important',
        },
        icon: {
          color: '#F8F5F2 !important',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(2n+1)': {
            backgroundColor: eonColorPallette.basicColors.white,
            borderBottom: '1px solid #f8f5f2',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: 'black',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        marginNormal: {
          marginTop: 0,
          marginBottom: 0,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&..makeStyles-bar': {
            padding: 0,
          },
        },
      },
    },
  },
});
